/**
 * 売上分析ページ (shadcn/ui移行完了)
 * @description サロンの売上データを包括的に分析・可視化する画面
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

// shadcn/ui components
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';

// 売上分析コンポーネント
import { ExecutiveSummary } from '../components/sales/ExecutiveSummary';
import { TrendAnalysis } from '../components/sales/TrendAnalysis';
import { PerformanceHeatmap } from '../components/sales/PerformanceHeatmap';
import { DetailedBreakdown } from '../components/sales/DetailedBreakdown';
import { MonthlyAnalysis } from '../components/sales/MonthlyAnalysis';

/**
 * 売上分析メインページ
 */
const Sales: React.FC = () => {
  usePageTitle('売上管理');
  const navigate = useNavigate();

  // TODO: 前年データ取得と前年同期比計算ロジックを実装
  // 現時点では undefined を渡し、UI上で "N/A" と表示される
  // 実データ接続時に、useDashboardData hook または専用 API から前年データを取得し、
  // 以下の計算式で成長率を算出する:
  // yoyGrowth = ((今日の売上 - 前年同日売上) / 前年同日売上) * 100
  // monthlyYoyGrowth = ((今月売上 - 前年同月売上) / 前年同月売上) * 100
  const yoyGrowth = undefined; // 前年同期比（日次）
  const monthlyYoyGrowth = undefined; // 前年同期比（月次）

  return (
    <div className="pb-6">
      {/* Content */}
      <div className="space-y-6">
        {/* Executive Summary - Full Width */}
        <ExecutiveSummary
          yoyGrowth={yoyGrowth}
          monthlyYoyGrowth={monthlyYoyGrowth}
        />

        {/* Monthly Analysis - Full Width */}
        <MonthlyAnalysis />

        {/* Trend Analysis - Full Width */}
        <TrendAnalysis />

        {/* Performance Heatmap - Full Width */}
        <PerformanceHeatmap />

        {/* Detailed Breakdown - Full Width */}
        <DetailedBreakdown />

        {/* AI Analysis Link Card */}
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="text-white h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">
                    AI分析で更なる改善提案を確認
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    AIが売上データを分析し、具体的な改善アクションを提案します
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/ai-analysis')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white min-h-[44px] px-6"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                AI分析を見る
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Sales;
